import { Link, NavLink, useNavigate } from "react-router-dom";
import { useState } from "react";
import { ShoppingBag, Menu, User, LogOut, LayoutDashboard } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import { Sheet, SheetContent, SheetTrigger, SheetTitle } from "@/components/ui/sheet";
import {
    DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";

const MARK = "https://static.prod-images.emergentagent.com/jobs/58fc8f05-e99b-4946-b4cb-e7f6df7eb0e5/images/02942df08251d3a60a1a48a0f056fbd5caa136e1de8ef9edbc656b4de3e64f5a.jpeg";

const LINKS = [
    { to: "/shop", label: "Shop" },
    { to: "/services", label: "Services" },
    { to: "/readings", label: "Readings" },
    { to: "/membership", label: "Membership" },
    { to: "/journal", label: "Journal" },
    { to: "/about", label: "About" },
    { to: "/contact", label: "Contact" },
];

export function Navbar() {
    const { count, setOpen } = useCart();
    const { user, logout } = useAuth();
    const [mobile, setMobile] = useState(false);
    const navigate = useNavigate();

    const linkClass = ({ isActive }) =>
        `text-sm tracking-wide transition-colors duration-300 hover:text-gold ${isActive ? "text-gold" : "text-ash"}`;

    return (
        <header className="fixed top-0 inset-x-0 z-50 backdrop-blur-xl bg-ink/80 border-b border-gold/10">
            <div className="max-w-7xl mx-auto px-5 sm:px-8 h-[72px] flex items-center justify-between gap-4">
                <Link to="/" data-testid="nav-logo" className="shrink-0 flex items-center gap-3">
                    <img src={MARK} alt="Sacred Earthworks" className="w-10 h-10 rounded-full ring-1 ring-gold/30 object-cover" />
                    <span className="leading-none">
                        <span className="block font-serif text-2xl text-cream tracking-wide">Sacred Earthworks</span>
                        <span className="hidden sm:block text-[9px] tracking-[0.3em] text-gold uppercase mt-1">The Fourth Bluff · Memphis</span>
                    </span>
                </Link>

                <nav className="hidden lg:flex items-center gap-8">
                    {LINKS.map((l) => (
                        <NavLink key={l.to} to={l.to} className={linkClass} data-testid={`nav-${l.label.toLowerCase()}`}>
                            {l.label}
                        </NavLink>
                    ))}
                </nav>

                <div className="flex items-center gap-2 sm:gap-4">
                    <button onClick={() => setOpen(true)} data-testid="nav-cart-button"
                        className="relative p-2 text-cream hover:text-gold transition-colors">
                        <ShoppingBag className="w-5 h-5" />
                        {count > 0 && (
                            <span data-testid="nav-cart-count" className="absolute -top-0.5 -right-0.5 bg-gold text-ink text-[10px] font-semibold w-4 h-4 rounded-full flex items-center justify-center">
                                {count}
                            </span>
                        )}
                    </button>

                    {user ? (
                        <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                                <button data-testid="nav-account-button" className="p-2 text-cream hover:text-gold transition-colors">
                                    <User className="w-5 h-5" />
                                </button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end" className="bg-surface2 border-gold/20 text-cream w-48">
                                <div className="px-2 py-1.5 text-xs text-faded truncate">{user.email}</div>
                                <DropdownMenuSeparator className="bg-gold/15" />
                                <DropdownMenuItem onClick={() => navigate("/account")} data-testid="menu-account" className="cursor-pointer focus:bg-gold/10 focus:text-gold">
                                    <User className="w-4 h-4 mr-2" /> My Account
                                </DropdownMenuItem>
                                {user.role === "admin" && (
                                    <DropdownMenuItem onClick={() => navigate("/admin")} data-testid="menu-admin" className="cursor-pointer focus:bg-gold/10 focus:text-gold">
                                        <LayoutDashboard className="w-4 h-4 mr-2" /> Admin Panel
                                    </DropdownMenuItem>
                                )}
                                <DropdownMenuSeparator className="bg-gold/15" />
                                <DropdownMenuItem onClick={async () => { await logout(); navigate("/"); }} data-testid="menu-logout" className="cursor-pointer focus:bg-gold/10 focus:text-gold">
                                    <LogOut className="w-4 h-4 mr-2" /> Sign Out
                                </DropdownMenuItem>
                            </DropdownMenuContent>
                        </DropdownMenu>
                    ) : (
                        <Link to="/login" data-testid="nav-login" className="hidden sm:inline-block text-sm px-4 py-2 rounded-full border border-gold/30 text-gold hover:bg-gold/10 transition-colors">
                            Sign In
                        </Link>
                    )}

                    <Sheet open={mobile} onOpenChange={setMobile}>
                        <SheetTrigger asChild>
                            <button className="lg:hidden p-2 text-cream" data-testid="nav-mobile-toggle"><Menu className="w-6 h-6" /></button>
                        </SheetTrigger>
                        <SheetContent side="right" className="bg-ink border-gold/15 w-72">
                            <SheetTitle className="sr-only">Menu</SheetTitle>
                            <div className="flex flex-col gap-6 mt-10">
                                {LINKS.map((l) => (
                                    <Link key={l.to} to={l.to} onClick={() => setMobile(false)} className="font-serif text-2xl text-cream hover:text-gold">
                                        {l.label}
                                    </Link>
                                ))}
                                {!user && <Link to="/login" onClick={() => setMobile(false)} className="text-gold text-lg">Sign In</Link>}
                            </div>
                        </SheetContent>
                    </Sheet>
                </div>
            </div>
        </header>
    );
}
